import { onAuthStateChanged, signOut as firebaseSignOut, type User } from 'firebase/auth';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { type AdminCapability, type AdminRole } from 'common/models/admin/rbac';

import { adminApiFetch } from './api';
import { type AuthCapabilities, capabilitiesForSession } from './auth-capabilities';
import { getFirebaseAuth } from './firebase';

interface AuthState {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  roles: AdminRole[];
  can: (capability: AdminCapability) => boolean;
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  isAdmin: false,
  error: null,
  signOut: async () => {},
  roles: [],
  can: () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [caps, setCaps] = useState<AuthCapabilities>({ roles: [], can: () => false });

  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // /adminStaff (the DB) is the source of truth: the server reads the caller's entry,
          // assigns their custom claim from it, and returns their roles. We do NOT read an existing
          // claim to decide access — admin status comes from the DB on every login.
          const session = await adminApiFetch('establishAdminSession', {});
          if (session.isAdmin) {
            // Refresh the ID token so subsequent admin-API calls carry the just-assigned claim.
            await firebaseUser.getIdToken(true);
            setUser(firebaseUser);
            setIsAdmin(true);
            setError(null);
            setCaps(capabilitiesForSession(session.roles));
          } else {
            await firebaseSignOut(auth);
            setUser(null);
            setIsAdmin(false);
            setError('You do not have admin privileges.');
            setCaps({ roles: [], can: () => false });
          }
        } catch {
          await firebaseSignOut(auth);
          setUser(null);
          setIsAdmin(false);
          setError('You do not have admin privileges.');
          setCaps({ roles: [], can: () => false });
        }
      } else {
        setUser(null);
        setIsAdmin(false);
        setError(null);
        setCaps({ roles: [], can: () => false });
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signOut = async () => {
    const auth = getFirebaseAuth();
    await firebaseSignOut(auth);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, isAdmin, error, signOut, roles: caps.roles, can: caps.can }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
